/**
 * 应用缓存管理工具
 * 统一存储格式：{ [thirdpartyAppId]: { name?: string; appId?: number } }
 * 以 userId 隔离，通过 initAppCache(userId) 在登录后注入。
 */

type AppCacheEntry = { name?: string; appId?: number };
type AppCache = Record<string, AppCacheEntry>;

let _userId: number | string | undefined;

/** 登录后调用一次，后续所有读写自动使用该 userId 对应的 key */
export function initAppCache(userId: number | string | undefined): void {
  _userId = userId;
}

function getCacheKey(): string {
  return _userId ? `app_cache_${_userId}` : 'app_cache';
}

function readCache(): AppCache {
  try {
    const raw = localStorage.getItem(getCacheKey());
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeCache(cache: AppCache): void {
  try {
    localStorage.setItem(getCacheKey(), JSON.stringify(cache));
  } catch (error) {
    console.error('Failed to write app cache:', error);
  }
}

/** 一次性写入 name 和 appId */
export function setAppCache(
  thirdpartyAppId: string | undefined,
  entry: AppCacheEntry,
): void {
  if (!thirdpartyAppId) return;
  const cache = readCache();
  cache[thirdpartyAppId] = { ...cache[thirdpartyAppId], ...entry };
  writeCache(cache);
}

/** 读取某个应用的完整缓存条目 */
export function getAppCache(
  thirdpartyAppId: string | undefined,
): AppCacheEntry | undefined {
  if (!thirdpartyAppId) return undefined;
  return readCache()[thirdpartyAppId];
}

// ── 向下兼容的单字段 API ──────────────────────────────────────────────────────

export function setAppName(
  thirdpartyAppId: string | undefined,
  name: string | undefined,
): void {
  if (!thirdpartyAppId || !name) return;
  setAppCache(thirdpartyAppId, { name });
}

export function getAppName(thirdpartyAppId: string): string | null {
  const entry = getAppCache(thirdpartyAppId);
  return entry?.name?.trim() || null;
}

export function setAppId(
  thirdpartyAppId: string | undefined,
  appId: number | undefined,
): void {
  if (!thirdpartyAppId || appId === undefined || appId === null) return;
  setAppCache(thirdpartyAppId, { appId });
}

export function getAppId(
  thirdpartyAppId: string | undefined,
): number | undefined {
  return getAppCache(thirdpartyAppId)?.appId;
}

// ── 批量 / 清理 API ───────────────────────────────────────────────────────────

/** 批量设置 name（兼容旧调用方） */
export function setAppNames(appNames: Record<string, string>): void {
  const cache = readCache();
  for (const [id, name] of Object.entries(appNames)) {
    cache[id] = { ...cache[id], name };
  }
  writeCache(cache);
}

/** 返回 { thirdpartyAppId: name } 映射（兼容旧调用方） */
export function getAllAppNames(): Record<string, string> {
  const cache = readCache();
  const result: Record<string, string> = {};
  for (const [id, entry] of Object.entries(cache)) {
    if (entry.name) result[id] = entry.name;
  }
  return result;
}

/** 删除某个应用的全部缓存 */
export function removeAppName(thirdpartyAppId: string): void {
  const cache = readCache();
  delete cache[thirdpartyAppId];
  writeCache(cache);
}

/** 清空当前用户的全部缓存 */
export function clearAllAppNames(): void {
  try {
    localStorage.removeItem(getCacheKey());
  } catch (error) {
    console.error('Failed to clear app cache:', error);
  }
}

// ── 迁移旧缓存 ────────────────────────────────────────────────────────────────

/**
 * 将旧的 app_names_cache / app_cache 迁移到当前用户的 key
 */
export function migrateOldCache(): void {
  try {
    const cache = readCache();
    let changed = false;

    // 旧 app_names_cache
    const oldNames = localStorage.getItem('app_names_cache');
    if (oldNames) {
      const names: Record<string, string> = JSON.parse(oldNames);
      for (const [id, name] of Object.entries(names)) {
        if (!cache[id]?.name) {
          cache[id] = { ...cache[id], name };
          changed = true;
        }
      }
      localStorage.removeItem('app_names_cache');
    }

    // 旧无 userId 前缀的 app_cache（仅在已有 userId 时迁移）
    if (_userId) {
      const oldKey = 'app_cache';
      const oldRaw = localStorage.getItem(oldKey);
      if (oldRaw) {
        const oldCache: AppCache = JSON.parse(oldRaw);
        for (const [id, entry] of Object.entries(oldCache)) {
          if (!cache[id]) {
            cache[id] = entry;
            changed = true;
          }
        }
        localStorage.removeItem(oldKey);
      }
    }

    if (changed) writeCache(cache);

    // 迁移旧主题 key：realDark → dark，并清理子应用遗留的冗余 key
    const savedTheme = localStorage.getItem('navTheme');
    if (savedTheme === 'realDark') {
      localStorage.setItem('navTheme', 'dark');
    }
    localStorage.removeItem('ragflow-ui-theme');
    localStorage.removeItem('vite-ui-theme');
  } catch (error) {
    console.error('Failed to migrate old cache:', error);
  }
}
